import React from 'react';

export type RowKeys = {
  /** One stable key per row, in render order. */
  keys: string[];
  /** Reorder the keys to match a move the caller is about to commit. */
  move: (from: number, to: number) => void;
  /** Drop the key of a row the caller is about to remove. */
  remove: (index: number) => void;
};

/**
 * Stable identities for rows of a list whose items carry none.
 *
 * The SEO lists hold plain strings and anonymous pairs, so their rows used to be
 * keyed by index, with the note that "rows have no identity beyond their
 * position". That stopped being true the moment the rows could be dragged: with
 * index keys React reuses row 2's inputs for whatever value lands at position 2,
 * so a reorder moves the text but leaves the caret, the selection and any
 * in-progress composition on the row that did not move.
 *
 * Keys are minted as the list grows and travel with their row through a move or
 * a removal, which is what makes a reorder a reorder rather than a rewrite of
 * every row at once. They are per-mount and never persisted: nothing outside
 * this component needs to name a row.
 */
export const useRowKeys = (count: number): RowKeys => {
  const seq = React.useRef(0);
  const keysRef = React.useRef<string[]>([]);

  // Reconcile with the array the parent holds. Growth appends, which is where
  // the add button puts a new row; a shrink the caller did not route through
  // `remove` (an article adopted into the form, a reset) just truncates, and the
  // rows are all new anyway.
  while (keysRef.current.length < count) {
    keysRef.current.push(`row-${seq.current}`);
    seq.current += 1;
  }
  if (keysRef.current.length > count) keysRef.current.length = count;

  const move = React.useCallback((from: number, to: number): void => {
    const next = keysRef.current.slice();
    const [key] = next.splice(from, 1);
    next.splice(to, 0, key);
    keysRef.current = next;
  }, []);

  const remove = React.useCallback((index: number): void => {
    keysRef.current = keysRef.current.filter((_, at) => at !== index);
  }, []);

  return { keys: keysRef.current, move, remove };
};

export default useRowKeys;
